import { UserPerformancesOnVaults, UserPerformancesOnVaultsRequestArgs } from "@decibeltrade/sdk";
import { FetchQueryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { DEFAULT_SHARE_PRICE } from "@/constants";
import { readDexSdk } from "@/contexts/dex-sdk";
import { useAuthState } from "@/hooks/use-auth-state";
import { addressesEqual } from "@/utils/address";
import { chainUnitsToAmount } from "@/utils/math";
import { getUnlockingEntries } from "@/utils/vault";

export function userPerformancesOnVaultsQueryKey(args: UserPerformancesOnVaultsRequestArgs) {
  return ["user-performances-on-vaults", args.ownerAddr] as const;
}

export function userPerformancesOnVaultsQuery(args: UserPerformancesOnVaultsRequestArgs) {
  return {
    queryKey: userPerformancesOnVaultsQueryKey({ ownerAddr: args.ownerAddr }),
    queryFn: async () => {
      if (!args.ownerAddr) return undefined;
      return await readDexSdk.vaults.getUserPerformancesOnVaults(args);
    },
  } satisfies FetchQueryOptions;
}

export function useUserPerformancesOnVaults() {
  const { subaccountAddress } = useAuthState();

  return useQuery({
    ...userPerformancesOnVaultsQuery({ ownerAddr: subaccountAddress ?? "" }),
    enabled: Boolean(subaccountAddress),
    refetchInterval: 90_000,
  });
}

export function useUserPerformanceOnVault(vaultAddress: string | undefined) {
  const { data: userPerformancesOnVaults } = useUserPerformancesOnVaults();

  // TODO: Uncomment this later, during debugging ran into differences in the share price
  // useVaultSharePrice(vaultAddress); // Updates vault share price on user performance every 15 seconds
  return useMemo(() => {
    const performance = userPerformancesOnVaults?.find((item) =>
      addressesEqual(item.vault.address, vaultAddress),
    );
    if (!performance) return undefined;
    return {
      ...performance,
      unlockingEntries: getUnlockingEntries(performance.deposits),
    };
  }, [userPerformancesOnVaults, vaultAddress]);
}

export function useSharePriceOnVault(vaultAddress: string | undefined) {
  const userPerformanceOnVault = useUserPerformanceOnVault(vaultAddress);
  return userPerformanceOnVault?.share_price ?? 1;
}

const vaultSharePriceQueryKey = (vaultAddress: string | undefined) =>
  ["vault-share-price", vaultAddress] as const;

export const useVaultSharePrice = (vaultAddress: string | undefined) => {
  const queryClient = useQueryClient();
  const { subaccountAddress } = useAuthState();

  return useQuery({
    queryKey: vaultSharePriceQueryKey(vaultAddress),
    queryFn: async () => {
      if (!vaultAddress) return DEFAULT_SHARE_PRICE;
      const vaultSharePrice = await readDexSdk.vaults.getVaultSharePrice({ vaultAddress });
      void queryClient.setQueryData(
        userPerformancesOnVaultsQueryKey({ ownerAddr: subaccountAddress ?? "" }),
        (old: UserPerformancesOnVaults | undefined) => {
          return old?.map((item) => {
            if (addressesEqual(item.vault.address, vaultAddress)) {
              const difference = vaultSharePrice - (item.share_price ?? 0);

              const currentNumShares = chainUnitsToAmount(item.current_num_shares ?? 0);

              const newAllTimeEarned = (item.all_time_earned ?? 0) + difference * currentNumShares;

              return {
                ...item,
                share_price: vaultSharePrice,
                current_value_of_shares: vaultSharePrice * currentNumShares,
                all_time_earned: newAllTimeEarned,
              };
            }
            return item;
          });
        },
      );
      return vaultSharePrice;
    },
    enabled: Boolean(subaccountAddress),
    refetchInterval: 20_000,
  });
};
